import { useEffect, useRef, type RefObject } from 'react';

/**
 * Dismiss an open overlay on Escape, and (when a `ref` is given) on a pointer
 * press outside it. The callback is read through a ref so passing an inline
 * handler doesn't re-subscribe the listeners on every render.
 */
export function useDismiss(
  open: boolean,
  onDismiss: () => void,
  ref?: RefObject<HTMLElement | null>,
): void {
  const cb = useRef(onDismiss);
  cb.current = onDismiss;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cb.current();
    };
    const onPointer = ref
      ? (e: MouseEvent) => {
          if (ref.current && !ref.current.contains(e.target as Node)) cb.current();
        }
      : undefined;

    document.addEventListener('keydown', onKey);
    if (onPointer) document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (onPointer) document.removeEventListener('mousedown', onPointer);
    };
  }, [open, ref]);
}
